import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable } from 'rxjs';
import { PRODUCT_URL } from '../shared/src';
import { ErrorHandlerService } from './error-handler.service';

export interface PlanogramType {
    id: number;
    name: string;
    countryIds: Array<{
        id: number;
        name: string;
    }>;
}

export interface PaginatorValue {
    totalData: number;
    from: number;
    to: number;
}

export interface PlanogramTypeListResponse {
    result: PlanogramType[];
    paginatorValue: PaginatorValue;
}

export interface PlanogramTypeMapping {
    id: number;
    countryIds: number[];
    brandIds: number[];
    updatedBy?: number | null;
    updatedByName?: string;
    updatedDateTime?: Date | string | null;
}

export interface ApiResponse {
    status: boolean;
    path: string;
    statusCode: number;
    result: PlanogramTypeListResponse;
    planogramTypeMappings?: PlanogramTypeMapping[];
}

export interface PlanogramTypeResponse {
    id: number;
    name: string;
    status: boolean;
    message: string;
    updatedByName?: string;
    updatedDateTime?: Date | string | null;
}

export interface CreatePlanogramTypeRequest {
    id: number;
    countryIds: number[];
    brandIds?: number[];
}

export interface UpdatePlanogramTypeRequest {
    id: number;
    name: string
}

@Injectable({
    providedIn: 'root'
})
export class PlanogramTypeService {
    private readonly PLANOGRAM_TYPE_API = `${PRODUCT_URL}/planogramType`;

    constructor(
        private http: HttpClient,
        private errorHandler: ErrorHandlerService
    ) { }

    /**
     * Get all planogram types with pagination, search, and status filtering
     * @param pageIndex Current page index
     * @param pageSize Number of items per page
     * @param searchValue Search filter value
     * @param status Status filter (optional)
     * @returns Observable of ApiResponse (wrapped by ResponseInterceptor)
     */
    getAll(pageIndex: number = 1, pageSize: number = 10, searchValue: string = '', status?: number): Observable<ApiResponse> {
        let params = new HttpParams()
            .set('pageIndex', pageIndex.toString())
            .set('pageSize', pageSize.toString());

        if (searchValue && searchValue.trim()) {
            params = params.set('searchFilter', searchValue.trim());
        }

        if (status !== undefined && status !== null) {
            params = params.set('status', status.toString());
        }

        return this.http
            .get<ApiResponse>(`${this.PLANOGRAM_TYPE_API}/getAllPlanogramTypes`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }



    getAllFixtureTypes(pageIndex?: number, pageSize?: number, searchValue?: string, status?: number): Observable<ApiResponse> {
        let params = new HttpParams()


        if (pageIndex) {
            params = params.set('pageIndex', pageIndex);
        }
        if (pageSize) {
            params = params.set('pageSize', pageSize);
        }
        if (searchValue && searchValue.trim()) {
            params = params.set('searchFilter', searchValue.trim());
        }

        if (status !== undefined && status !== null) {
            params = params.set('status', status.toString());
        }

        return this.http
            .get<ApiResponse>(`${this.PLANOGRAM_TYPE_API}/getAllFixtureTypes`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Create a new planogram type
     * @param data Planogram type data with name and country IDs
     * @returns Observable of PlanogramTypeResponse
     */
    create(data: CreatePlanogramTypeRequest): Observable<PlanogramTypeResponse> {
        return this.http
            .post<PlanogramTypeResponse>(`${this.PLANOGRAM_TYPE_API}/create`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    createFixtureType(data: { name: string }): Observable<PlanogramTypeResponse> {
        return this.http
            .post<PlanogramTypeResponse>(`${this.PLANOGRAM_TYPE_API}/createFixtureType`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Update an existing planogram type
     * @param data Planogram type data with id, name and country IDs
     * @returns Observable of PlanogramTypeResponse
     */
    updateFixtureType(data: UpdatePlanogramTypeRequest): Observable<PlanogramTypeResponse> {
        return this.http
            .put<PlanogramTypeResponse>(`${this.PLANOGRAM_TYPE_API}/updateFixtureType`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }


    update(data: any): Observable<PlanogramTypeResponse> {
        return this.http
            .put<PlanogramTypeResponse>(`${this.PLANOGRAM_TYPE_API}/update`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Delete a planogram type
     * @param id Planogram type ID to delete
     * @returns Observable of PlanogramTypeResponse
     */
    delete(id: number): Observable<PlanogramTypeResponse> {
        return this.http
            .delete<PlanogramTypeResponse>(`${this.PLANOGRAM_TYPE_API}/delete/${id}`)
            .pipe(catchError(this.errorHandler.handleError));
    }
}
